import React, { useCallback, useMemo, useState } from 'react';
import { type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useToggle } from 'usehooks-ts';

import {
  ProcessDTO,
  ProcessQueryDTO,
  ProcessResponseDTO,
  useFilterProcessesInfinite,
  useGetProcessesHook
} from '@/api/generated';
import DataGrid from '@/components/DataGrid/DataGrid';
import { AutocompleteWide } from '@/components/fields/Autocomplete';
import Form from '@/components/Form/Form';
import StickyHead from '@/components/StickyHead/StickyHead';
import { useEntityList } from '@/hooks/useEntityList';
import { usePageTitle } from '@/hooks/usePageTitle';
import { CREATE_MODAL_PARAM, toProcess } from '@/root/linker';
import { CreateProcessModal } from '@/views/CreateProcessModal/CreateProcessModal';
import FilterButton from '@/views/Filters/FilterButton';
import FilterPanel from '@/views/Filters/FilterPanel';
import SectionProcess from '@/views/Filters/SectionProcess';

import { COMPOUND_FORM_VALUE_KEYS, DEFAULT_VALUES, URL_CONFIG_MAP } from './constants';
import NewProcessButton from './NewProcessButton';
import { processColumns } from './processColumns';

const Processes = () => {
  const theme = useTheme();
  const { t } = useTranslation(['processes', 'filters']);
  const autosuggestProcesses = useGetProcessesHook();
  const [queryBody, setQueryBody] = useState<ProcessQueryDTO | null>(null);
  const [showFilters, toggleShowFilters] = useToggle();
  const [searchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(!!searchParams.get(CREATE_MODAL_PARAM));

  usePageTitle(t('layouts.processes.title'));

  const {
    data: processesData,
    isError,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useFilterProcessesInfinite(queryBody ?? {}, {
    query: { suspense: false, enabled: !!queryBody }
  });

  const processesList = useMemo(
    () => (processesData?.pages?.flatMap(page => page) || []) as ProcessResponseDTO[],
    [processesData]
  );

  const handleFetchNextPage = useCallback(() => {
    // TODO: Pagination is turned off in case of error. Later we should at least notify the user that there was one.
    if (isError || isFetchingNextPage || !hasNextPage || !processesData?.pages?.length) return;
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isError, isFetchingNextPage, processesData?.pages?.length]);

  const { sorting, handleChangeSort, form } = useEntityList<FieldValues, ProcessQueryDTO>(
    DEFAULT_VALUES as FieldValues,
    URL_CONFIG_MAP,
    setQueryBody,
    undefined,
    300
  );

  const handleSearchProcesses = useCallback(
    async (query: string) => {
      try {
        const searchResults = await autosuggestProcesses({ search: query });

        return searchResults.map(({ id, name }: ProcessDTO) => ({
          label: name!,
          value: id!,
          to: toProcess({ processId: id! })
        }));
      } catch (e) {
        return [];
      }
    },
    [autosuggestProcesses]
  );

  const handleOpenCreateModal = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const columns = useMemo(() => processColumns(theme, t), [theme, t]);

  return (
    <Box sx={{ px: 5, flexGrow: 1 }} component="div">
      <StickyHead sx={{ paddingTop: theme.spacing(2.25) }}>
        <Stack direction="row" mb={3} alignItems="center" justifyContent="space-between">
          <Typography variant="h2">{t('layouts.processes.title')}</Typography>
        </Stack>
        <Form form={form} className="Filters-root">
          <Stack direction="row" justifyContent="space-between" sx={{ marginTop: theme.spacing(3) }}>
            <AutocompleteWide
              placeholder={t('layouts.processes.autosuggest.placeholder')}
              loadOptions={handleSearchProcesses}
            />

            <Stack direction="row" alignItems="center" gap={2}>
              <FilterButton
                compoundFormKeys={COMPOUND_FORM_VALUE_KEYS}
                isOpen={showFilters}
                onClick={toggleShowFilters}
              />
              <NewProcessButton onClick={handleOpenCreateModal} />
            </Stack>
          </Stack>

          <FilterPanel isOpen={showFilters}>
            <SectionProcess name="process" />
          </FilterPanel>
        </Form>
      </StickyHead>
      <DataGrid
        data={processesList || []}
        columns={columns}
        onSort={handleChangeSort}
        isLoading={isLoading || !queryBody}
        isLoadingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        sorting={sorting}
        resizeWatchSelector=".Filters-root"
        hasError={isError && !processesList.length}
        onLoadNextPage={handleFetchNextPage}
      />
      {showCreateModal && <CreateProcessModal onClose={handleCloseCreateModal} />}
    </Box>
  );
};

export default Processes;
